import axios from 'axios';
import { API_URL } from '../config';

const whatsappWebApi = axios.create({
  baseURL: `${API_URL}/api/whatsapp-web`,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

whatsappWebApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

whatsappWebApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// Shared Baileys / WhatsApp Web (tipos legados, continuam funcionando)
// =============================================================================

export interface WhatsAppWebNumber {
  id: number;
  label: string;
  phone_number?: string | null;
  normalized_phone?: string | null;
  status: string;
  is_active: boolean;
  is_default: boolean;
  daily_limit: number;
  minute_limit: number;
  sent_today_count: number;
  last_connected_at?: string | null;
  last_disconnected_at?: string | null;
  last_healthcheck_at?: string | null;
  last_error?: string | null;
  created_by_id?: number | null;
  created_at: string;
  updated_at: string;
  // Cloud API (Meta) -- access_token nunca vem cru, só a existência + preview mascarado.
  provider: 'baileys' | 'cloud_api';
  phone_number_id?: string | null;
  business_account_id?: string | null;
  has_access_token: boolean;
  access_token_preview?: string | null;
}

export interface WhatsAppWebNumberCreateData {
  label: string;
  phone_number?: string;
  daily_limit?: number;
  minute_limit?: number;
  is_default?: boolean;
  // Preencher os três juntos cria um número Cloud API; deixar de fora cria Baileys (default).
  phone_number_id?: string;
  access_token?: string;
  business_account_id?: string;
}

export interface WhatsAppWebNumberUpdateData {
  label?: string;
  phone_number?: string;
  daily_limit?: number;
  minute_limit?: number;
  is_active?: boolean;
  is_default?: boolean;
  // access_token ausente/vazio = não mudar o token já salvo.
  phone_number_id?: string;
  access_token?: string;
  business_account_id?: string;
}

export interface WhatsAppWebSession {
  id: number;
  number_id: number;
  session_key: string;
  browser_profile_path: string;
  status: string;
  qr_code_available: boolean;
  qr_code_expires_at?: string | null;
  connected_at?: string | null;
  disconnected_at?: string | null;
  last_seen_at?: string | null;
  retry_count: number;
  last_error?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WhatsAppWebStartSessionResponse {
  success: boolean;
  number_id: number;
  session_id: number;
  session_key: string;
  status: string;
  detail: string;
}

export interface WhatsAppWebQrResponse {
  success: boolean;
  number_id: number;
  session_id?: number | null;
  status: string;
  qr_code?: string | null;
  qr_code_expires_at?: string | null;
  detail?: string | null;
}

export interface WhatsAppRecentChatItem {
  title: string;
  subtitle?: string | null;
  unread_count: number;
  last_time?: string | null;
}

export interface WhatsAppRecentChatsResponse {
  success: boolean;
  number_id: number;
  count: number;
  items: WhatsAppRecentChatItem[];
}

export interface WhatsAppQueueItem {
  id: number;
  campaign_id?: number | null;
  lead_id: number;
  lead_nome?: string | null;
  lead_status?: string | null;
  sender_number_id?: number | null;
  conversation_id?: number | null;
  priority: number;
  status: string;
  scheduled_at?: string | null;
  processed_at?: string | null;
  retry_count: number;
  max_retries: number;
  last_error?: string | null;
  payload?: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

export interface WhatsAppQueueEnqueueResponse {
  success: boolean;
  queued: number;
  skipped: number;
  items: WhatsAppQueueItem[];
}

export interface WhatsAppQueueProcessResponse {
  success: boolean;
  processed: number;
  sent: number;
  failed: number;
  results: Array<Record<string, any>>;
}

export interface WhatsAppQueueBatchStartResponse {
  success: boolean;
  started: boolean;
  already_running: boolean;
  detail?: string | null;
}

export interface WhatsAppQueueBatchStatus {
  running: boolean;
  started_at?: string | null;
  finished_at?: string | null;
  processed: number;
  sent: number;
  cancelled: number;
  failed: number;
  stop_reason?: string | null;
}

export interface WhatsAppConversation {
  id: number;
  lead_id?: number | null;
  sender_number_id: number;
  campaign_id?: number | null;
  remote_phone: string;
  remote_name?: string | null;
  status: string;
  origin: string;
  last_message_at?: string | null;
  last_inbound_at?: string | null;
  last_outbound_at?: string | null;
  unread_count: number;
  assigned_consultor_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface WhatsAppMessage {
  id: number;
  conversation_id: number;
  sender_number_id?: number | null;
  lead_id?: number | null;
  campaign_id?: number | null;
  direction: string;
  message_type: string;
  content?: string | null;
  status: string;
  sent_at?: string | null;
  received_at?: string | null;
  created_at: string;
}

export interface WhatsAppConversationSyncResponse {
  success: boolean;
  conversation: WhatsAppConversation;
  scraped_count: number;
  inbound_messages: number;
  new_messages: number;
}

export interface WhatsAppReplyResponse {
  success: boolean;
  conversation_id: number;
  message_id?: number | null;
  normalized_to?: string | null;
  status: string;
}

// =============================================================================
// Cloud API — AW-008 novos tipos replicando schemas Python de W1/W2/W3
// =============================================================================

export type WhatsAppInboundEventSource = 'meta_webhook' | 'baileys_bridge';
export type WhatsAppInboundEventResult =
  | 'signature_invalid'
  | 'app_secret_missing'
  | 'verify_token_mismatch'
  | 'no_matching_phone_number_id'
  | 'provider_mismatch'
  | 'discarded_duplicate_external_message_id'
  | 'ingested'
  | 'parse_error'
  | 'bridge_unreachable'
  | 'unknown_error';

/** Summary (listagem): NÃO expõe payload_preview / error_detail (privacidade). */
export interface WhatsAppInboundEventLogSummary {
  id: number;
  created_at: string;
  organization_id: number;
  number_id: number | null;
  source: WhatsAppInboundEventSource;
  event_result: WhatsAppInboundEventResult;
  metadata_phone_number_id: string | null;
  remote_phone_normalized: string | null;
  external_message_id: string | null;
}

/** Detail (único evento por id): EXPOE payload_preview + error_detail. */
export interface WhatsAppInboundEventLogDetail extends WhatsAppInboundEventLogSummary {
  payload_preview: string | null;
  error_detail: string | null;
}

export interface WhatsAppCloudBusinessProfile {
  name?: string | null;
  about?: string | null;
  address?: string | null;
  description?: string | null;
  email?: string | null;
  profile_picture_handle?: string | null;
  websites?: string[] | null;
  vertical?: string | null;
}

export interface WhatsAppCloudGlobalFlags {
  send_provider: string;
  paid_sending_enabled: boolean;
  app_secret_set: boolean;
  verify_token_set: boolean;
}

export interface WhatsAppCloudHealthChecks {
  credentials_ok: boolean;
  business_profile_ok: boolean;
  last_webhook_ingested_at: string | null;
  last_inbound_error_event: WhatsAppInboundEventLogSummary | null;
}

export interface WhatsAppCloudInboxCounters {
  total_conversations: number;
  unread_count: number;
  waiting_human_count: number;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
}

export interface WhatsAppCloudNumberStatusResponse {
  number: WhatsAppWebNumber;
  business_profile: WhatsAppCloudBusinessProfile | null;
  global_flags: WhatsAppCloudGlobalFlags;
  health_checks: WhatsAppCloudHealthChecks;
  inbox_counters: WhatsAppCloudInboxCounters;
}

export interface WhatsAppCloudSendResponse {
  success: boolean;
  conversation_id: number | null;
  message_id: number | null;
  provider_used: string | null;
  paid_sending_was_blocked: boolean;
  normalized_to: string | null;
  error: string | null;
  detail: string | null;
}

export interface WhatsAppCloudSendRequest {
  to: string;
  content: string;
  message_type?: 'text';
  number_id?: number | null;
}

export interface ListCloudInboundEventsParams {
  page?: number;
  per_page?: number;
  number_id?: number | null;
  event_result?: WhatsAppInboundEventResult[] | null;
  source?: WhatsAppInboundEventSource | null;
  date_from?: string | null;
  date_to?: string | null;
  remote_phone?: string | null;
}

export interface WhatsAppCloudInboundEventListResponse {
  items: WhatsAppInboundEventLogSummary[];
  total: number;
  page: number;
  per_page: number;
}

// =============================================================================
// Cliente Cloud API separado (mesmo padrão do whatsappWebApi mas base diferente)
// =============================================================================

const whatsappCloudApi = axios.create({
  baseURL: `${API_URL}/api/whatsapp-cloud`,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

whatsappCloudApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);
whatsappCloudApi.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// Funções legadas WhatsApp Web / Baileys
// =============================================================================

export const listNumbers = async (): Promise<WhatsAppWebNumber[]> => {
  const response = await whatsappWebApi.get('/numbers');
  return response.data;
};

export const createNumber = async (data: WhatsAppWebNumberCreateData): Promise<WhatsAppWebNumber> => {
  const response = await whatsappWebApi.post('/numbers', data);
  return response.data;
};

export const updateNumber = async (numberId: number, data: WhatsAppWebNumberUpdateData): Promise<WhatsAppWebNumber> => {
  const response = await whatsappWebApi.put(`/numbers/${numberId}`, data);
  return response.data;
};

export const startNumberSession = async (numberId: number): Promise<WhatsAppWebStartSessionResponse> => {
  const response = await whatsappWebApi.post(`/numbers/${numberId}/start`);
  return response.data;
};

export const getNumberSessionStatus = async (numberId: number): Promise<WhatsAppWebSession> => {
  const response = await whatsappWebApi.get(`/numbers/${numberId}/status`);
  return response.data;
};

export const getNumberQr = async (numberId: number): Promise<WhatsAppWebQrResponse> => {
  const response = await whatsappWebApi.get(`/numbers/${numberId}/qr`);
  return response.data;
};

export const disconnectNumberSession = async (numberId: number) => {
  const response = await whatsappWebApi.post(`/numbers/${numberId}/disconnect`);
  return response.data;
};

export const listRecentChats = async (numberId: number, onlyUnread: boolean = false): Promise<WhatsAppRecentChatsResponse> => {
  const response = await whatsappWebApi.get(`/numbers/${numberId}/recent-chats`, {
    params: { only_unread: onlyUnread },
  });
  return response.data;
};

export const enqueueCampaignQueue = async (
  campaignId: number,
  data: { lead_ids?: number[]; sender_number_id?: number; skip_already_sent?: boolean; limit?: number }
): Promise<WhatsAppQueueEnqueueResponse> => {
  const response = await whatsappWebApi.post(`/campaigns/${campaignId}/queue`, data);
  return response.data;
};

export const listQueueItems = async (status?: string): Promise<WhatsAppQueueItem[]> => {
  const response = await whatsappWebApi.get('/queue', {
    params: { status },
  });
  return response.data;
};

export const processQueue = async (senderNumberId?: number): Promise<WhatsAppQueueBatchStartResponse> => {
  const response = await whatsappWebApi.post('/queue/process', null, {
    params: {
      sender_number_id: senderNumberId,
    },
  });
  return response.data;
};

export const getQueueBatchStatus = async (senderNumberId?: number): Promise<WhatsAppQueueBatchStatus> => {
  const response = await whatsappWebApi.get('/queue/batch-status', {
    params: {
      sender_number_id: senderNumberId,
    },
  });
  return response.data;
};

export const listConversations = async (params?: {
  status?: string;
  unread_only?: boolean;
  sender_number_id?: number;
  limit?: number;
}): Promise<WhatsAppConversation[]> => {
  const response = await whatsappWebApi.get('/conversations', { params });
  return response.data;
};

export const listConversationMessages = async (conversationId: number, limit: number = 100): Promise<WhatsAppMessage[]> => {
  const response = await whatsappWebApi.get(`/conversations/${conversationId}/messages`, {
    params: { limit },
  });
  return response.data;
};

export const syncConversation = async (conversationId: number, limit: number = 50): Promise<WhatsAppConversationSyncResponse> => {
  const response = await whatsappWebApi.post(`/conversations/${conversationId}/sync`, null, {
    params: { limit },
  });
  return response.data;
};

export const replyToConversation = async (conversationId: number, text: string): Promise<WhatsAppReplyResponse> => {
  const response = await whatsappWebApi.post(`/conversations/${conversationId}/reply`, { text });
  return response.data;
};

// =============================================================================
// Funções Cloud API (AW-008 / AW-007 endpoints)
// =============================================================================

export const getCloudNumberStatus = async (
  numberId?: number | null
): Promise<WhatsAppCloudNumberStatusResponse> => {
  const { data } = await whatsappCloudApi.get('/number/status', {
    params: numberId != null ? { number_id: numberId } : undefined,
  });
  return data;
};

export const sendCloudNumberText = async (
  payload: WhatsAppCloudSendRequest
): Promise<WhatsAppCloudSendResponse> => {
  const { data } = await whatsappCloudApi.post('/number/send', payload);
  return data;
};

export const listCloudInboundEvents = async (
  params: ListCloudInboundEventsParams = {}
): Promise<WhatsAppCloudInboundEventListResponse> => {
  const { data } = await whatsappCloudApi.get('/number/inbound-events', {
    params: {
      page: params.page,
      per_page: params.per_page,
      number_id: params.number_id ?? undefined,
      event_result: params.event_result ?? undefined,
      source: params.source ?? undefined,
      date_from: params.date_from ?? undefined,
      date_to: params.date_to ?? undefined,
      remote_phone: params.remote_phone ?? undefined,
    },
    paramsSerializer: { indexes: null },
  });
  return data;
};

export const getCloudInboundEventDetail = async (
  eventId: number
): Promise<WhatsAppInboundEventLogDetail> => {
  const { data } = await whatsappCloudApi.get(`/number/inbound-events/${eventId}`);
  return data;
};

// =============================================================================
// Objetos agregados (exportação default + cloud separado)
// =============================================================================

const whatsappWebService = {
  listNumbers,
  createNumber,
  updateNumber,
  startNumberSession,
  getNumberSessionStatus,
  getNumberQr,
  disconnectNumberSession,
  listRecentChats,
  enqueueCampaignQueue,
  listQueueItems,
  processQueue,
  getQueueBatchStatus,
  listConversations,
  listConversationMessages,
  syncConversation,
  replyToConversation,
};

export const whatsappCloudService = {
  getCloudNumberStatus,
  sendCloudNumberText,
  listCloudInboundEvents,
  getCloudInboundEventDetail,
};

export default whatsappWebService;
